// ============================================================================
// COLLUSION DETECTION SERVICE - Player Relationship Analysis
// ============================================================================
// This service tracks player relationships over time and detects
// patterns that may indicate collusion (soft play, coordinated action).
// Creates FLAGS for human review - NO auto-bans.
// ============================================================================

import {
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  query,
  where,
  getDocs,
  Timestamp,
  increment,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  PlayerRelationship,
  HandParticipation,
  PlayerBetSummary,
  TableJoinData,
  FLAG_THRESHOLDS,
  COLLUSION_SIGNAL_WEIGHTS,
} from '../../types/securityTypes';
import securityFlagService from './securityFlagService';
import deviceFingerprintService from './deviceFingerprintService';

// ============================================================================
// CONSTANTS
// ============================================================================

const RELATIONSHIPS_COLLECTION = 'playerRelationships';

// Analysis thresholds
const MIN_HANDS_FOR_ANALYSIS = 20;        // Need at least 20 hands to analyze
const ANALYSIS_FREQUENCY_HANDS = 10;       // Analyze every 10 hands per pair
const HIGH_SAME_TABLE_FREQUENCY = 0.7;     // 70%+ together = suspicious
const HIGH_FOLD_RATE = 0.8;                // 80%+ fold rate = suspicious
const HIGH_WIN_RATE_VS_PLAYER = 0.85;      // 85%+ win rate against specific player

// ============================================================================
// COLLUSION DETECTION SERVICE CLASS
// ============================================================================

class CollusionDetectionService {
  // ============================================================================
  // TABLE JOIN TRACKING
  // ============================================================================

  /**
   * Record when a player joins a table
   * Tracks who plays with whom over time
   */
  async recordTableJoin(data: TableJoinData): Promise<void> {
    try {
      const { tableId, playerId, currentPlayerIds, timestamp, playerIp } = data;

      console.log(`📊 [Collusion] Recording table join: ${playerId.substring(0, 8)} at ${tableId.substring(0, 8)}`);

      // Check for same IP at table if IP is provided
      if (playerIp) {
        const ipHash = await deviceFingerprintService.hashIP(playerIp);
        await deviceFingerprintService.checkSameIPAtTable(
          tableId,
          playerId,
          currentPlayerIds,
          ipHash
        );
      }

      // Update relationship with each current player
      for (const otherId of currentPlayerIds) {
        if (otherId === playerId) continue;
        await this.incrementTablesPlayedTogether(playerId, otherId);
      }

    } catch (error) {
      console.error('❌ [Collusion] Error recording table join:', error);
    }
  }

  // ============================================================================
  // HAND PARTICIPATION TRACKING
  // ============================================================================

  /**
   * Record hand participation data
   * Called at the end of each hand
   */
  async recordHandParticipation(data: HandParticipation): Promise<void> {
    try {
      const { tableId, handNumber, playerIds, winnerIds, betHistory, timestamp } = data;

      console.log(`📊 [Collusion] Recording hand #${handNumber}: ${playerIds.length} players`);

      // Update relationships for all player pairs in this hand
      for (let i = 0; i < playerIds.length; i++) {
        for (let j = i + 1; j < playerIds.length; j++) {
          const player1 = playerIds[i];
          const player2 = playerIds[j];

          // Find their bet data
          const bet1 = betHistory.find(b => b.playerId === player1);
          const bet2 = betHistory.find(b => b.playerId === player2);

          if (!bet1 || !bet2) continue;

          // Update relationship
          await this.updateRelationshipFromHand(
            player1,
            player2,
            bet1,
            bet2,
            winnerIds.includes(player1),
            winnerIds.includes(player2)
          );
        }
      }

    } catch (error) {
      console.error('❌ [Collusion] Error recording hand participation:', error);
    }
  }

  // ============================================================================
  // RELATIONSHIP MANAGEMENT
  // ============================================================================

  /**
   * Get or create player relationship document
   */
  private async getOrCreateRelationship(
    player1Id: string,
    player2Id: string
  ): Promise<PlayerRelationship> {
    const relationshipId = this.getRelationshipId(player1Id, player2Id);
    const relationRef = doc(db, RELATIONSHIPS_COLLECTION, relationshipId);
    const relationDoc = await getDoc(relationRef);

    if (relationDoc.exists()) {
      return relationDoc.data() as PlayerRelationship;
    }

    // Create new relationship
    const [p1, p2] = this.sortPlayerIds(player1Id, player2Id);
    const newRelationship: PlayerRelationship = {
      player1Id: p1,
      player2Id: p2,
      tablesPlayedTogether: 0,
      handsPlayedTogether: 0,
      player1FoldsToPlayer2: 0,
      player2FoldsToPlayer1: 0,
      player1WinsVsPlayer2: 0,
      player2WinsVsPlayer1: 0,
      netChipFlow: 0,
      lastPlayedTogether: Timestamp.now(),
      sameIpOccurrences: 0,
      sameDeviceOccurrences: 0,
      collusionScore: 0,
      lastAnalyzed: Timestamp.now(),
    };

    await setDoc(relationRef, newRelationship);
    return newRelationship;
  }

  /**
   * Generate consistent relationship ID for two players
   */
  private getRelationshipId(player1Id: string, player2Id: string): string {
    const [p1, p2] = this.sortPlayerIds(player1Id, player2Id);
    return `${p1}_${p2}`;
  }

  /**
   * Sort player IDs alphabetically for consistency
   */
  private sortPlayerIds(player1Id: string, player2Id: string): [string, string] {
    return player1Id < player2Id 
      ? [player1Id, player2Id] 
      : [player2Id, player1Id];
  }

  /**
   * Increment tables played together count
   */
  private async incrementTablesPlayedTogether(
    player1Id: string,
    player2Id: string
  ): Promise<void> {
    const relationshipId = this.getRelationshipId(player1Id, player2Id);
    const relationRef = doc(db, RELATIONSHIPS_COLLECTION, relationshipId);

    try {
      const relationDoc = await getDoc(relationRef);

      if (relationDoc.exists()) {
        await updateDoc(relationRef, {
          tablesPlayedTogether: increment(1),
          lastPlayedTogether: Timestamp.now(),
        });
      } else {
        await this.getOrCreateRelationship(player1Id, player2Id);
        await updateDoc(relationRef, {
          tablesPlayedTogether: increment(1),
        });
      }
    } catch (error) {
      console.error('Error incrementing tables played:', error);
    }
  }

  /**
   * Update relationship based on hand result
   */
  private async updateRelationshipFromHand(
    player1Id: string,
    player2Id: string,
    bet1: PlayerBetSummary,
    bet2: PlayerBetSummary,
    player1Won: boolean,
    player2Won: boolean
  ): Promise<void> {
    const relationshipId = this.getRelationshipId(player1Id, player2Id);
    const relationRef = doc(db, RELATIONSHIPS_COLLECTION, relationshipId);
    const [p1, p2] = this.sortPlayerIds(player1Id, player2Id);

    try {
      // Ensure relationship exists
      await this.getOrCreateRelationship(player1Id, player2Id);

      const updates: any = {
        handsPlayedTogether: increment(1),
        lastPlayedTogether: Timestamp.now(),
      };

      // Track folds
      if (bet1.folded && bet1.foldedToPlayerId === player2Id) {
        if (player1Id === p1) {
          updates.player1FoldsToPlayer2 = increment(1);
        } else {
          updates.player2FoldsToPlayer1 = increment(1);
        }
      }
      if (bet2.folded && bet2.foldedToPlayerId === player1Id) {
        if (player2Id === p1) {
          updates.player1FoldsToPlayer2 = increment(1);
        } else {
          updates.player2FoldsToPlayer1 = increment(1);
        }
      }

      // Track wins
      if (player1Won) {
        if (player1Id === p1) {
          updates.player1WinsVsPlayer2 = increment(1);
        } else {
          updates.player2WinsVsPlayer1 = increment(1);
        }
      }
      if (player2Won) {
        if (player2Id === p1) {
          updates.player1WinsVsPlayer2 = increment(1);
        } else {
          updates.player2WinsVsPlayer1 = increment(1);
        }
      }

      // Track chip flow
      if (player1Won && !player2Won) {
        const flow = bet2.totalBet;
        if (player1Id === p1) {
          updates.netChipFlow = increment(-flow); // Flow to p1 (negative for p2)
        } else {
          updates.netChipFlow = increment(flow); // Flow to p2 (positive)
        }
      } else if (player2Won && !player1Won) {
        const flow = bet1.totalBet;
        if (player2Id === p2) {
          updates.netChipFlow = increment(flow); // Flow to p2
        } else {
          updates.netChipFlow = increment(-flow); // Flow to p1
        }
      }

      await updateDoc(relationRef, updates);

      // Check if we should run analysis
      const relationship = await this.getOrCreateRelationship(player1Id, player2Id);
      if (relationship.handsPlayedTogether % ANALYSIS_FREQUENCY_HANDS === 0 &&
          relationship.handsPlayedTogether >= MIN_HANDS_FOR_ANALYSIS) {
        await this.analyzePlayerPair(player1Id, player2Id);
      }

    } catch (error) {
      console.error('Error updating relationship from hand:', error);
    }
  }

  // ============================================================================
  // COLLUSION ANALYSIS
  // ============================================================================

  /**
   * Analyze relationship between two players for collusion signals
   */
  async analyzePlayerPair(player1Id: string, player2Id: string): Promise<number> {
    try {
      console.log(`🔍 [Collusion] Analyzing pair: ${player1Id.substring(0, 8)} + ${player2Id.substring(0, 8)}`);

      const relationship = await this.getOrCreateRelationship(player1Id, player2Id);

      if (relationship.handsPlayedTogether < MIN_HANDS_FOR_ANALYSIS) {
        console.log(`ℹ️ [Collusion] Not enough hands (${relationship.handsPlayedTogether})`);
        return 0;
      }

      let score = 0;
      const signals: string[] = [];
      const dataPoints: any[] = [];

      // Check same device/IP
      const shareDevice = await deviceFingerprintService.doUsersShareDevice(player1Id, player2Id);
      const shareIP = await deviceFingerprintService.doUsersShareIP(player1Id, player2Id);

      if (shareDevice) {
        score += COLLUSION_SIGNAL_WEIGHTS.SAME_DEVICE;
        signals.push('SAME_DEVICE');
        dataPoints.push({
          type: 'same_device',
          description: 'Players share a device fingerprint',
          value: true,
          weight: COLLUSION_SIGNAL_WEIGHTS.SAME_DEVICE,
        });
      }

      if (shareIP) {
        score += COLLUSION_SIGNAL_WEIGHTS.SAME_IP;
        signals.push('SAME_IP');
        dataPoints.push({
          type: 'same_ip',
          description: 'Players share an IP address',
          value: true,
          weight: COLLUSION_SIGNAL_WEIGHTS.SAME_IP,
        });
      }

      // Calculate fold patterns
      const totalFoldsP1toP2 = relationship.player1FoldsToPlayer2;
      const totalFoldsP2toP1 = relationship.player2FoldsToPlayer1;
      const handsPlayed = relationship.handsPlayedTogether;

      const foldRateP1toP2 = handsPlayed > 0 ? totalFoldsP1toP2 / handsPlayed : 0;
      const foldRateP2toP1 = handsPlayed > 0 ? totalFoldsP2toP1 / handsPlayed : 0;

      if (foldRateP1toP2 > HIGH_FOLD_RATE || foldRateP2toP1 > HIGH_FOLD_RATE) {
        score += COLLUSION_SIGNAL_WEIGHTS.ONE_ALWAYS_FOLDS;
        signals.push('ONE_ALWAYS_FOLDS');
        
        const higherRate = Math.max(foldRateP1toP2, foldRateP2toP1);
        dataPoints.push({
          type: 'fold_pattern',
          description: `One player folds to the other ${Math.round(higherRate * 100)}% of the time`,
          value: higherRate,
          weight: COLLUSION_SIGNAL_WEIGHTS.ONE_ALWAYS_FOLDS,
        });
      }

      // Check for consistent chip flow (potential dumping)
      const avgChipFlow = Math.abs(relationship.netChipFlow) / handsPlayed;
      if (avgChipFlow > 0 && Math.abs(relationship.netChipFlow) > 1000) {
        score += COLLUSION_SIGNAL_WEIGHTS.CHIP_TRANSFER_PATTERN;
        signals.push('CHIP_TRANSFER_PATTERN');
        dataPoints.push({
          type: 'chip_flow',
          description: `Consistent chip flow: ${relationship.netChipFlow > 0 ? 'P2' : 'P1'} gains`,
          value: relationship.netChipFlow,
          weight: COLLUSION_SIGNAL_WEIGHTS.CHIP_TRANSFER_PATTERN,
        });
      }

      // Normalize score to 0-100
      const maxPossibleScore = Object.values(COLLUSION_SIGNAL_WEIGHTS).reduce((a, b) => a + b, 0);
      const normalizedScore = Math.min(100, Math.round((score / maxPossibleScore) * 100));

      console.log(`📊 [Collusion] Score: ${normalizedScore}% (${signals.length} signals)`);

      // Update relationship with score
      const relationshipId = this.getRelationshipId(player1Id, player2Id);
      const relationRef = doc(db, RELATIONSHIPS_COLLECTION, relationshipId);
      await updateDoc(relationRef, {
        collusionScore: normalizedScore,
        lastAnalyzed: Timestamp.now(),
      });

      // Create flag if threshold met
      if (normalizedScore >= FLAG_THRESHOLDS.COLLUSION_MEDIUM) {
        const hasExisting = await securityFlagService.hasExistingFlag(
          'collusion',
          player1Id,
          player2Id,
          168 // 1 week
        );

        if (!hasExisting) {
          const severity = normalizedScore >= FLAG_THRESHOLDS.COLLUSION_HIGH ? 'high' : 'medium';

          await securityFlagService.createFlag({
            type: 'collusion',
            severity,
            confidence: normalizedScore,
            primaryUserId: player1Id,
            relatedUserId: player2Id,
            evidence: {
              signals,
              dataPoints,
              summary: `Collusion score ${normalizedScore}%: ${signals.join(', ')}`,
            },
          });
        }
      }

      return normalizedScore;

    } catch (error) {
      console.error('❌ [Collusion] Error analyzing player pair:', error);
      return 0;
    }
  }

  /**
   * Get relationship data for two players
   */
  async getRelationship(player1Id: string, player2Id: string): Promise<PlayerRelationship | null> {
    try {
      const relationshipId = this.getRelationshipId(player1Id, player2Id);
      const relationRef = doc(db, RELATIONSHIPS_COLLECTION, relationshipId);
      const relationDoc = await getDoc(relationRef);

      if (relationDoc.exists()) {
        return relationDoc.data() as PlayerRelationship;
      }

      return null;

    } catch (error) {
      console.error('❌ [Collusion] Error getting relationship:', error);
      return null;
    }
  }

  /**
   * Get all suspicious relationships for a player
   */
  async getSuspiciousRelationships(
    playerId: string,
    minScore: number = 50
  ): Promise<PlayerRelationship[]> {
    try {
      // Query relationships where player is involved with high score
      const q1 = query(
        collection(db, RELATIONSHIPS_COLLECTION),
        where('player1Id', '==', playerId),
        where('collusionScore', '>=', minScore)
      );

      const q2 = query(
        collection(db, RELATIONSHIPS_COLLECTION),
        where('player2Id', '==', playerId),
        where('collusionScore', '>=', minScore)
      );

      const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);

      const relationships: PlayerRelationship[] = [];
      snap1.docs.forEach(doc => relationships.push(doc.data() as PlayerRelationship));
      snap2.docs.forEach(doc => relationships.push(doc.data() as PlayerRelationship));

      return relationships;

    } catch (error) {
      console.error('❌ [Collusion] Error getting suspicious relationships:', error);
      return [];
    }
  }

  /**
   * Get all high-score relationships (for admin dashboard)
   */
  async getHighScoreRelationships(
    minScore: number = 60,
    limitCount: number = 50
  ): Promise<PlayerRelationship[]> {
    try {
      const q = query(
        collection(db, RELATIONSHIPS_COLLECTION),
        where('collusionScore', '>=', minScore)
      );

      const snapshot = await getDocs(q);
      const relationships: PlayerRelationship[] = [];
      
      snapshot.docs.forEach(doc => {
        relationships.push(doc.data() as PlayerRelationship);
      });

      // Sort by score descending
      relationships.sort((a, b) => b.collusionScore - a.collusionScore);

      return relationships.slice(0, limitCount);

    } catch (error) {
      console.error('❌ [Collusion] Error getting high score relationships:', error);
      return [];
    }
  }

  /**
   * Force re-analysis of a player pair
   */
  async forceReanalyze(player1Id: string, player2Id: string): Promise<number> {
    return this.analyzePlayerPair(player1Id, player2Id);
  }

  /**
   * Record same IP occurrence between two players
   */
  async recordSameIPOccurrence(player1Id: string, player2Id: string): Promise<void> {
    try {
      const relationshipId = this.getRelationshipId(player1Id, player2Id);
      const relationRef = doc(db, RELATIONSHIPS_COLLECTION, relationshipId);

      await this.getOrCreateRelationship(player1Id, player2Id);

      await updateDoc(relationRef, {
        sameIpOccurrences: increment(1),
      });

    } catch (error) {
      console.error('Error recording same IP occurrence:', error);
    }
  }

  /**
   * Record same device occurrence between two players
   */
  async recordSameDeviceOccurrence(player1Id: string, player2Id: string): Promise<void> {
    try {
      const relationshipId = this.getRelationshipId(player1Id, player2Id);
      const relationRef = doc(db, RELATIONSHIPS_COLLECTION, relationshipId);

      await this.getOrCreateRelationship(player1Id, player2Id);

      await updateDoc(relationRef, {
        sameDeviceOccurrences: increment(1),
      });

    } catch (error) {
      console.error('Error recording same device occurrence:', error);
    }
  }
}

// ============================================================================
// SINGLETON EXPORT
// ============================================================================

const collusionDetectionService = new CollusionDetectionService();
export { collusionDetectionService };
export default collusionDetectionService;